import { defineStore } from 'pinia'
import { computed, inject, provide, ref } from 'vue'
import { useRhythmicLogger } from '#imports'

export type MaybePromise<T = void> = T | Promise<T>

export type ReactiveDbDisposer = () => MaybePromise<void>

export interface ReactiveDbContextRecord {
  id: string
  label?: string
  stop: ReactiveDbDisposer
}

export interface RegisterReactiveDbContextOptions {
  id?: string
  label?: string
}

export interface ReactiveDbPaneScope {
  paneId: string
  registerDisposer: (stop: ReactiveDbDisposer, options?: RegisterReactiveDbContextOptions) => string
  releaseDisposer: (id: string) => void
  terminate: (ids?: string[]) => Promise<void>
}

const createId = (() => {
  let counter = 0
  return (prefix = 'ctx') => {
    counter += 1
    const globalCrypto = (globalThis as typeof globalThis & { crypto?: Crypto }).crypto
    if (globalCrypto && typeof globalCrypto.randomUUID === 'function') return `${prefix}-${globalCrypto.randomUUID()}`
    return `${prefix}-${Date.now()}-${counter}`
  }
})()

const useReactiveDbContextRegistry = defineStore('reactive-db-context-registry', () => {
  const logger = useRhythmicLogger('reactive-db-context-registry')
  const panes = ref<Record<string, Record<string, ReactiveDbContextRecord>>>({})

  function ensurePaneMap(paneId: string) {
    if (!panes.value[paneId]) panes.value[paneId] = {}
    return panes.value[paneId]
  }

  function registerContext(paneId: string, record: Omit<ReactiveDbContextRecord, 'id'> & { id?: string }) {
    const targetId = record.id ?? createId('pane')
    const paneMap = ensurePaneMap(paneId)
    paneMap[targetId] = {
      id: targetId,
      label: record.label,
      stop: record.stop,
    }
    panes.value = { ...panes.value }
    logger.debug?.('Registered reactive db context', {
      paneId,
      id: targetId,
      label: record.label,
    })
    return targetId
  }

  function releaseContext(paneId: string, id: string) {
    const paneMap = panes.value[paneId]
    if (!paneMap) return
    delete paneMap[id]
    if (Object.keys(paneMap).length === 0) delete panes.value[paneId]
    panes.value = { ...panes.value }
    logger.debug?.('Released reactive db context', { paneId, id })
  }

  async function terminateContexts(paneId: string, ids?: string[]) {
    const paneMap = panes.value[paneId]
    if (!paneMap) return
    const targetIds = ids && ids.length > 0 ? ids : Object.keys(paneMap)
    await Promise.all(
      targetIds.map(async (id) => {
        const record = paneMap[id]
        if (!record) return
        try {
          await record.stop()
        } catch (error) {
          logger.warn?.('Failed to terminate reactive db context', {
            paneId,
            id,
            label: record.label,
            error,
          })
        } finally {
          delete paneMap[id]
        }
      }),
    )
    if (Object.keys(paneMap).length === 0) delete panes.value[paneId]
    panes.value = { ...panes.value }
  }

  const paneSummaries = computed(() => {
    return Object.entries(panes.value).map(([paneId, records]) => ({
      paneId,
      count: Object.keys(records).length,
      labels: Object.values(records)
        .map((entry) => entry.label)
        .filter(Boolean) as string[],
    }))
  })

  /**
   * Terminate all contexts across all panes.
   * Call this when the user logs out to ensure all streams are terminated.
   */
  async function terminateAllContexts() {
    const allPaneIds = Object.keys(panes.value)
    logger.debug?.('Terminating all reactive db contexts', {
      paneCount: allPaneIds.length,
    })
    await Promise.all(allPaneIds.map((paneId) => terminateContexts(paneId)))
  }

  return {
    registerContext,
    releaseContext,
    terminateContexts,
    terminateAllContexts,
    paneSummaries,
  }
})

export default useReactiveDbContextRegistry

export const reactiveDbPaneScopeKey = Symbol('reactive-db-pane-scope')

export function createReactiveDbPaneScope(paneId: string): ReactiveDbPaneScope {
  const registry = useReactiveDbContextRegistry()

  return {
    paneId,
    registerDisposer(stop, options) {
      return registry.registerContext(paneId, {
        id: options?.id,
        label: options?.label,
        stop,
      })
    },
    releaseDisposer(id) {
      registry.releaseContext(paneId, id)
    },
    async terminate(ids) {
      await registry.terminateContexts(paneId, ids)
    },
  }
}

export function provideReactiveDbPaneScope(paneId: string) {
  const scope = createReactiveDbPaneScope(paneId)
  provide(reactiveDbPaneScopeKey, scope)
  return scope
}

export function useReactiveDbPaneScope() {
  return inject<ReactiveDbPaneScope | null>(reactiveDbPaneScopeKey, null)
}
